import {useEffect} from 'react'
import {useAuth} from './useAuth'
import {societyService} from '../services'
import {clearPendingSocietyJoin, peekPendingSocietyJoin} from '../utils/pendingSocietyJoin'

// Module-level so Layout remounts across route changes can't start a second redemption; the
// in-flight guard plus backend idempotency are the further backstops.
let redemptionInFlight = false

/**
 * Redeems a stashed society join code once the user is authenticated (ADR-093 addendum). Mounted
 * in the authenticated Layout so it fires on the first page after any auth flow — including OAuth
 * full-page redirects and the profile-completion hard navigation.
 */
export const usePendingSocietyJoin = (): void => {
    const {isAuthenticated} = useAuth()

    useEffect(() => {
        if (!isAuthenticated || redemptionInFlight) return
        // Peek, don't take: the stash is cleared only once we know the outcome, so a transient
        // failure (offline, 5xx) leaves the code in place to retry on the next navigation instead
        // of silently dropping the pending join.
        const code = peekPendingSocietyJoin()
        if (!code) return
        redemptionInFlight = true
        societyService.joinWithCode(code)
            .then(() => {
                clearPendingSocietyJoin()
            })
            .catch((e) => {
                // A 404 means the code is definitively unusable (rotated/deleted/non-society) —
                // drop it so it can't loop. Any other failure keeps the stash for a silent retry.
                if ((e as {response?: {status?: number}}).response?.status === 404) {
                    clearPendingSocietyJoin()
                }
            })
            .finally(() => {
                redemptionInFlight = false
            })
    }, [isAuthenticated])
}
